import React, { useCallback, useRef } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  BoardFilterDateRange,
  BoardFilterView,
  GetPaginatedAppointmentsQuery,
  GetUserBoardFilterSettingsDocument,
  useGetUserBoardFilterSettingsQuery,
  useUpdateUserBoardFilterSettingsMutation,
} from 'shared/types/graphql';
import { VStack } from '@televet/kibble-ui/build/chakra';
import BoardHeader from './components/Header/Container';
import useUnreadMessageCount from 'shared/hooks/useUnreadMessageCount';
import ChannelStatusBoard from './components/Boards/ChannelStatusBoard';
import AutomationStatusBoard from 'pages/Board/components/Boards/AutomationStatusBoard';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { useAppDispatch } from 'state/hooks';
import { updateBoardState } from './state/boardSlice';
import { useToast } from '@televet/kibble-ui';
import { dateRangeMap, mapDateToPredefinedOption } from './utils/dateRangeUtils';
import { openAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';

export type Appointment = NonNullable<GetPaginatedAppointmentsQuery['appointments']>[0];

const Board = (): JSX.Element => {
  const { currentClinicId, clinicUserId } = useClinicUser();
  const boardContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [updateBoardSettings] = useUpdateUserBoardFilterSettingsMutation({
    refetchQueries: [GetUserBoardFilterSettingsDocument],
    onError: (e) => {
      console.error(e);
      toast({
        title: 'We are having trouble saving your settings.',
        description: e,
      });
    },
  });

  const { data: userBoardSettings, loading: isLoadingSettings } = useGetUserBoardFilterSettingsQuery({
    variables: {
      where: {
        userId: clinicUserId || '',
      },
    },
    onCompleted: (data) => {
      if (data) {
        const boardSettings = data?.customFindUniqueUserBoardFilter;
        const mappedOption = mapDateToPredefinedOption({
          gte: boardSettings?.specificDateRange?.gte,
          lte: boardSettings?.specificDateRange?.lte,
        });

        // No option selected, No date range selected = default to today
        let _dateRangeOption: BoardFilterDateRange | null = BoardFilterDateRange.Today;
        let _specificDateRange = dateRangeMap.today;

        // No option selected, Yes date range selected = default to date range selected
        if (!boardSettings?.dateRangeOption && boardSettings?.specificDateRange) {
          _dateRangeOption = mappedOption;
          _specificDateRange = boardSettings?.specificDateRange;
        }

        // Yes option selected, Yes date range selected = default to date range selected
        if (boardSettings?.dateRangeOption && boardSettings?.specificDateRange) {
          _dateRangeOption = mappedOption;
          _specificDateRange = boardSettings?.specificDateRange;
        }

        // Yes option selected, No date range selected = default to option
        if (boardSettings?.dateRangeOption && !boardSettings?.specificDateRange) {
          _dateRangeOption = boardSettings.dateRangeOption;
          _specificDateRange = dateRangeMap[boardSettings.dateRangeOption];
        }

        dispatch(
          updateBoardState({
            boardSettings: {
              collapsedColumnsMap: boardSettings?.collapsedColumnsMap || {},
              selectedChannelStatusIdsMap: boardSettings?.selectedChannelStatusIdsMap || {},
              shouldHideEmptyColumns: boardSettings?.shouldHideEmptyColumns || false,
              shouldCollapseNoStatusColumn: boardSettings?.shouldCollapseNoStatusColumn || false,
              currentView: boardSettings?.currentView || BoardFilterView.Conversations,
              dateRangeOption: _dateRangeOption,
              specificDateRange: _specificDateRange,
            },
            currentClinicId,
            clinicUserId,
          }),
        );
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
      });
    },
    skip: !clinicUserId,
  });

  const {
    data: { unreadChannelArray: unreadChannelIds },
  } = useUnreadMessageCount();

  const onEditAppointment = useCallback((appointment?: Partial<Appointment>): void => {
    dispatch(openAppointmentModal({ appointmentId: appointment?.id }));
  }, [dispatch]);

  return (
    <VStack
      className="Board"
      ref={boardContainerRef}
      spacing={0}
      align="flex-start"
      justify="flex-start"
      overflow="hidden"
      h="100%"
    >
      <BoardHeader updateBoardSettings={updateBoardSettings} />
      {isLoadingSettings || !currentClinicId || !userBoardSettings ? (
        <Spinner isFullScreen />
      ) : userBoardSettings?.customFindUniqueUserBoardFilter?.currentView === BoardFilterView.Automations ? (
        <AutomationStatusBoard
          clinicId={currentClinicId}
          unreadChannelIds={unreadChannelIds}
          onEditAppointment={onEditAppointment}
          updateBoardSettings={updateBoardSettings}
        />
      ) : (
            <ChannelStatusBoard
              clinicId={currentClinicId}
              unreadChannelIds={unreadChannelIds}
              onEditAppointment={onEditAppointment}
              updateBoardSettings={updateBoardSettings}
            />
          )}
    </VStack>
  );
};

export default Board;
